import { addonsContentType, ConnectedBuyFormatCurrency } from '@buy/connectedComponents'
import { OrderSummaryLine } from '@buy/state/order-summary/order-summary'
import React, { FunctionComponent, useEffect, useState } from 'react'
import { Tooltip } from '@common/components'
import { calculateVolumeDiscount, getPrice } from '@buy/utils/volume-components-helpers'
import {
    BillingPeriod,
    BillingPeriodCouponPaymentPlanMapper,
    COUPON_DISCOUNT_TYPE,
    COUPON_TYPE,
} from '@common/constants'
import { first, isEmpty } from 'lodash-es'
import { CouponServerData } from '@common/state/coupon/state'
import { processCouponCalculations, ValidCoupon } from '@buy/utils'
import styles from '../../connectedComponents/OrderSummary/order-summary.module.css'
import { BillingTenureValue } from '../BillingPeriodSwitch'
import { CouponDiscountSummary } from '../CouponDiscountSummary'

interface DiscountSummaryProps {
    baseLine: OrderSummaryLine
    additionalLines: Array<OrderSummaryLine>
    addonLines: Array<OrderSummaryLine>
    title: string
    isAnnual: boolean
    annualDiscountLabel: string
    addonsLabels: addonsContentType
    billingPeriodKeys: string[]
    showAdditionalLines: boolean
    percentageOffPromoDiscountLabel: string
    setTotalDiscount: (total: number) => void
    totalDiscount: number
    promoDiscountForAnnualSavingsTooltipLabel: string
    couponServerData: CouponServerData | null
    billingTenure: BillingTenureValue
    currency: string
    validCoupons: ValidCoupon[]
    setValidCoupons: (data: ValidCoupon[]) => void
    setCouponValidAndApplied: (isValid: boolean) => void
    rePriceDiscounts: Record<string, number>
}

export const DiscountSummary: FunctionComponent<DiscountSummaryProps> = ({
    baseLine,
    additionalLines,
    addonLines,
    title,
    isAnnual,
    annualDiscountLabel,
    addonsLabels,
    billingPeriodKeys,
    showAdditionalLines,
    percentageOffPromoDiscountLabel,
    setTotalDiscount,
    totalDiscount,
    promoDiscountForAnnualSavingsTooltipLabel,
    couponServerData,
    billingTenure,
    currency,
    validCoupons,
    setValidCoupons,
    setCouponValidAndApplied,
    rePriceDiscounts,
}) => {
    const discountLineClasses = `${styles.small} ${styles.teal}`
    const [annualDiscount, setAnnualDiscount] = useState<number>(0)
    const [volumeDiscount, setVolumeDiscount] = useState<number>(0)
    const [couponDiscount, setCouponDiscount] = useState<number>(0)

    const calculateAnnualDiscount = () => {
        const leftToggleBillingPeriod = billingPeriodKeys.find((item) => item !== BillingPeriod.ANNUAL)!
        const priceWithoutDiscount = getPrice(1, baseLine.allPrices[leftToggleBillingPeriod]) * baseLine.multiplier * 12
        const annualPrice = getPrice(1, baseLine.allPrices[BillingPeriod.ANNUAL]) * baseLine.multiplier * 12
        setAnnualDiscount(priceWithoutDiscount - annualPrice)
    }

    const getVolumeDiscount = () => {
        const baseLineVolumeDiscount = calculateVolumeDiscount(
            baseLine,
            baseLine.multiplier,
            billingPeriodKeys,
            isAnnual
        )
        // get addon volume discount
        let addonVolumeDiscountSum = 0
        if (!isEmpty(addonLines)) {
            addonLines.forEach((item) => {
                const addonVolumeDiscount = calculateVolumeDiscount(item, item.quantity, billingPeriodKeys, isAnnual)
                addonVolumeDiscountSum += addonVolumeDiscount
            })
        }
        // get additional lines volume discount
        let additionalVolumeDiscountSum = 0
        if (!isEmpty(additionalLines)) {
            additionalLines.forEach((item) => {
                const additionalLineVolumeDiscount = calculateVolumeDiscount(
                    item,
                    item.multiplier,
                    billingPeriodKeys,
                    isAnnual
                )
                additionalVolumeDiscountSum += additionalLineVolumeDiscount
            })
        }
        let finalVolumeDiscount = 0
        if (!showAdditionalLines) finalVolumeDiscount = baseLineVolumeDiscount + addonVolumeDiscountSum
        else finalVolumeDiscount = additionalVolumeDiscountSum + addonVolumeDiscountSum
        setVolumeDiscount(finalVolumeDiscount)
    }

    useEffect(() => {
        if (!isEmpty(couponServerData) && couponServerData.isCouponValid) {
            if (
                !isEmpty(rePriceDiscounts) &&
                rePriceDiscounts[billingTenure.selected] &&
                couponServerData.couponDiscountType === COUPON_DISCOUNT_TYPE.REPRICE
            ) {
                setValidCoupons([
                    {
                        discount: rePriceDiscounts[billingTenure.selected],
                        sku: baseLine.key, // currently reprice is supported only for base plan skus, need to change this in the future when we start supporting reprice coupons for addons
                        percent: 0,
                        isReprice: true,
                    },
                ])
                return
            }
            let isValid = true
            if (couponServerData.couponType === COUPON_TYPE.FIX) {
                const skusSelected = [baseLine, ...addonLines].map((item) => item.key)
                const couponItemsSkus = couponServerData.couponItems
                    .filter((item) => item.paymentPlan === BillingPeriodCouponPaymentPlanMapper[billingTenure.selected])
                    .map((item) => item.productSku)
                isValid = couponItemsSkus.every((item) => skusSelected.includes(item))
            }
            setCouponValidAndApplied(isValid)
            if (isValid) {
                const baseLineCouponData: ValidCoupon = processCouponCalculations(
                    baseLine,
                    couponServerData.couponDiscountType,
                    billingTenure.selected,
                    couponServerData.couponItems,
                    currency
                )
                const addonLinesCouponData = addonLines.map((item) => {
                    const result = processCouponCalculations(
                        item,
                        couponServerData.couponDiscountType,
                        billingTenure.selected,
                        couponServerData.couponItems,
                        currency,
                        true
                    )
                    return result
                })
                setValidCoupons([baseLineCouponData, ...addonLinesCouponData])
            } else setValidCoupons([])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [couponServerData, baseLine, addonLines, billingTenure, rePriceDiscounts])

    useEffect(() => {
        if (billingPeriodKeys.length > 1 && isAnnual) {
            calculateAnnualDiscount()
        } else setAnnualDiscount(0)
        getVolumeDiscount()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseLine, isAnnual, showAdditionalLines, addonLines])

    useEffect(() => {
        const couponDiscountSum = validCoupons.reduce((sum, currentValue) => {
            if (!currentValue.isReprice) return sum + currentValue.discount
            return sum
        }, 0)
        setCouponDiscount(couponDiscountSum)
    }, [validCoupons])

    useEffect(() => {
        setTotalDiscount(annualDiscount + volumeDiscount + couponDiscount)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [annualDiscount, volumeDiscount, couponDiscount])

    return (
        <>
            {totalDiscount > 0 && (
                <div className={styles.section}>
                    <div className={styles.row} data-qat="discount-summary">
                        <span className={styles['item-title']} data-qat="line-item-name">
                            {title}
                        </span>
                        <span className={styles.item} data-qat="line-item-subtotal">
                            -<ConnectedBuyFormatCurrency value={totalDiscount} />
                        </span>
                    </div>
                    {annualDiscount > 0 && (
                        <div key="annual-discount" className={styles['sub-section']} data-qat="annual-discount">
                            <div className={styles.row}>
                                <span className={discountLineClasses} data-qat="annual-savings-label">
                                    {annualDiscountLabel}
                                </span>
                                <span className={discountLineClasses} data-qat="annual-discount-price">
                                    -<ConnectedBuyFormatCurrency value={annualDiscount} />
                                </span>
                            </div>
                        </div>
                    )}
                    {volumeDiscount > 0 && (
                        <div key="volume-discount" className={styles['sub-section']} data-qat="volume-discount">
                            <div className={styles.row}>
                                <span className={discountLineClasses}>
                                    {addonsLabels.totalVolumeDiscount}
                                    <Tooltip
                                        content={addonsLabels.tooltips.totalVolumeDiscountTooltip}
                                        additionalClass={styles['order-tooltip']}
                                        contentClass={styles['order-tooltip-content']}
                                    />
                                </span>
                                <span className={discountLineClasses} data-qat="volume-discount-price">
                                    -<ConnectedBuyFormatCurrency value={volumeDiscount} />
                                </span>
                            </div>
                        </div>
                    )}

                    {couponDiscount > 0 ? (
                        <CouponDiscountSummary
                            percentageOffPromoDiscountLabel={percentageOffPromoDiscountLabel}
                            promoDiscountForAnnualSavingsTooltipLabel={promoDiscountForAnnualSavingsTooltipLabel}
                            couponDiscount={couponDiscount}
                            sku=""
                            percent={first(validCoupons)?.percent || 0}
                            billingTenure={billingTenure}
                        />
                    ) : (
                        <></>
                    )}
                </div>
            )}
        </>
    )
}
